import React, { createContext, useContext, useReducer, useEffect } from 'react';
import type { ReactNode } from 'react';
import { authApi } from '../services/auth';
import { tokenStorage } from '../utils/tokenStorage';
import { getErrorMessage } from '../utils/error-handling';
import { useToast } from './ToastContext';
import type {
  UserProfile,
  LoginCredentials,
  RegisterData,
  UpdateProfileData,
} from '../types/auth';

// Auth state interface
interface AuthState {
  user: UserProfile | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

// Auth actions
type AuthAction =
  | { type: 'AUTH_START' }
  | { type: 'AUTH_SUCCESS'; payload: UserProfile }
  | { type: 'AUTH_FAILURE'; payload: string }
  | { type: 'AUTH_LOGOUT' }
  | { type: 'CLEAR_ERROR' }
  | { type: 'SET_LOADING'; payload: boolean };

// Auth context interface
interface AuthContextType extends AuthState {
  login: (credentials: LoginCredentials, rememberMe?: boolean) => Promise<void>;
  register: (userData: RegisterData) => Promise<void>;
  activate: (token: string) => Promise<void>;
  resendActivation: () => Promise<void>;
  logout: () => void;
  refreshToken: () => Promise<void>;
  updateProfile: (data: UpdateProfileData) => Promise<void>;
  clearError: () => void;
  refreshProfile: () => Promise<void>;
}

// Initial state
const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
};

// Auth reducer
const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'AUTH_START':
      return {
        ...state,
        isLoading: true,
        error: null,
      };
    case 'AUTH_SUCCESS':
      return {
        ...state,
        user: action.payload,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      };
    case 'AUTH_FAILURE':
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload,
      };
    case 'AUTH_LOGOUT':
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      };
    default:
      return state;
  }
};

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Auth provider props
interface AuthProviderProps {
  children: ReactNode;
}

// Auth provider component
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const { showError, showSuccess } = useToast();

  // Initialize auth state on mount
  useEffect(() => {
    const initializeAuth = async () => {
      const storedUser = tokenStorage.getUser();
      const isAuthenticated = tokenStorage.isAuthenticated();

      if (isAuthenticated && storedUser) {
        try {
          // Verify token is still valid by fetching profile
          const response = await authApi.getProfile();
          if (response.data.success && response.data.data) {
            dispatch({ type: 'AUTH_SUCCESS', payload: response.data.data });
          } else {
            // Token invalid, clear storage
            tokenStorage.clearTokens();
            dispatch({ type: 'AUTH_LOGOUT' });
          }
        } catch {
          // Token invalid or expired, clear storage
          tokenStorage.clearTokens();
          dispatch({ type: 'AUTH_LOGOUT' });
        }
      } else {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    };

    initializeAuth();
  }, []);

  // Login function
  const login = async (
    credentials: LoginCredentials,
    rememberMe: boolean = false,
  ): Promise<void> => {
    try {
      dispatch({ type: 'AUTH_START' });

      const response = await authApi.login(credentials);

      if (response.data.success && response.data.data) {
        const { user, accessToken, refreshToken } = response.data.data;

        // Store tokens and user data with remember me preference
        tokenStorage.setTokens({ accessToken, refreshToken }, rememberMe);
        tokenStorage.setUser(user);

        dispatch({ type: 'AUTH_SUCCESS', payload: user });
        showSuccess('Successfully logged in!');
      } else {
        throw new Error('Login failed');
      }
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      dispatch({ type: 'AUTH_FAILURE', payload: errorMessage });
      showError(errorMessage);
      throw error;
    }
  };

  // Register function
  const register = async (userData: RegisterData): Promise<void> => {
    try {
      dispatch({ type: 'AUTH_START' });

      const response = await authApi.register(userData);

      if (response.data.success && response.data.data) {
        const { user, accessToken, refreshToken } = response.data.data;

        // Store tokens and user data
        tokenStorage.setTokens({ accessToken, refreshToken });
        tokenStorage.setUser(user);

        dispatch({ type: 'AUTH_SUCCESS', payload: user });
        showSuccess('Account created successfully! Welcome!');
      } else {
        throw new Error('Registration failed');
      }
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      dispatch({ type: 'AUTH_FAILURE', payload: errorMessage });
      showError(errorMessage);
      throw error;
    }
  };

  // Activate function
  const activate = async (token: string): Promise<void> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const response = await authApi.activate(token);
      if (response.data.success && response.data.data) {
        dispatch({ type: 'AUTH_SUCCESS', payload: response.data.data.user });
        showSuccess('Konto erfolgreich aktiviert!');
      } else {
        throw new Error('Aktivierung fehlgeschlagen');
      }
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      showError(errorMessage);
      throw error;
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  // Resend activation function
  const resendActivation = async (): Promise<void> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const response = await authApi.resendActivation();
      if (response.data.success) {
        showSuccess('Aktivierungs-E-Mail wurde erneut gesendet!');
      } else {
        throw new Error('Senden fehlgeschlagen');
      }
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      showError(errorMessage);
      throw error;
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  // Logout function
  const logout = (): void => {
    try {
      // Call logout API (fire and forget)
      authApi.logout().catch(() => {
        // Ignore errors on logout API call
      });
    } finally {
      // Always clear local storage and state
      tokenStorage.clearTokens();
      dispatch({ type: 'AUTH_LOGOUT' });
      showSuccess('Successfully logged out');
    }
  };

  // Refresh token function
  const refreshToken = async (): Promise<void> => {
    try {
      const storedRefreshToken = tokenStorage.getRefreshToken();

      if (!storedRefreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await authApi.refreshToken(storedRefreshToken);

      if (response.data.success && response.data.data) {
        const {
          user,
          accessToken,
          refreshToken: newRefreshToken,
        } = response.data.data;

        // Update stored tokens
        tokenStorage.setTokens({ accessToken, refreshToken: newRefreshToken });
        tokenStorage.setUser(user);

        dispatch({ type: 'AUTH_SUCCESS', payload: user });
      } else {
        throw new Error('Token refresh failed');
      }
    } catch (error) {
      // Refresh failed, logout user
      tokenStorage.clearTokens();
      dispatch({ type: 'AUTH_LOGOUT' });
      throw error;
    }
  };

  // Update profile function
  const updateProfile = async (data: UpdateProfileData): Promise<void> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });

      let passwordChanged = false;
      // Handle password change first if password fields are provided
      if (data.currentPassword && data.newPassword) {
        await authApi.changePassword(data.currentPassword, data.newPassword);
        passwordChanged = true;
      }

      // Copy data and remove password fields to prevent backend whitelist validation error
      const profileData = { ...data };
      delete profileData.currentPassword;
      delete profileData.newPassword;

      const currentUser = tokenStorage.getUser();
      const emailChanged = !!(
        profileData.email &&
        currentUser &&
        profileData.email !== currentUser.email
      );

      if (emailChanged) {
        const response = await authApi.updateProfile(profileData);

        if (response.data.success && response.data.data) {
          const updatedUser = response.data.data;

          // Update stored user data
          tokenStorage.setUser(updatedUser);

          dispatch({ type: 'AUTH_SUCCESS', payload: updatedUser });
          if (passwordChanged) {
            showSuccess('Profile and password updated successfully!');
          } else {
            showSuccess('Profile updated successfully!');
          }
        } else {
          throw new Error('Profile update failed');
        }
      } else {
        // No email changes, but password was changed
        if (passwordChanged) {
          showSuccess('Password updated successfully!');
        }
        if (currentUser) {
          dispatch({ type: 'AUTH_SUCCESS', payload: currentUser });
        } else {
          dispatch({ type: 'SET_LOADING', payload: false });
        }
      }
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      dispatch({ type: 'AUTH_FAILURE', payload: errorMessage });
      showError(errorMessage);
      throw error;
    }
  };

  // Clear error function
  const clearError = (): void => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  // Refresh profile function
  const refreshProfile = React.useCallback(async (): Promise<void> => {
    try {
      const response = await authApi.getProfile();
      if (response.data.success && response.data.data) {
        tokenStorage.setUser(response.data.data);
        dispatch({ type: 'AUTH_SUCCESS', payload: response.data.data });
      }
    } catch (error) {
      console.error('Failed to refresh profile', error);
    }
  }, []);

  // Context value
  const contextValue: AuthContextType = {
    ...state,
    login,
    register,
    activate,
    resendActivation,
    logout,
    refreshToken,
    updateProfile,
    clearError,
    refreshProfile,
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
};

export default AuthContext;
